/**
 * SandboxConfigEditor — Edit the sandbox's opencode.json configuration.
 *
 * Form fields for key config options, pushed to running sandbox via API.
 */
import { useState, useEffect } from "react";
import { Settings, Save, AlertCircle } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { useToast } from "@/components/ui/Toast";
import { useSandboxStore } from "@/stores/useSandboxStore";

/* ------------------------------------------------------------------ */
/*  Model presets                                                      */
/* ------------------------------------------------------------------ */

const MODEL_PRESETS = [
  { value: "anthropic/claude-sonnet-4-20250514", label: "Claude Sonnet 4" },
  { value: "anthropic/claude-opus-4-20250514", label: "Claude Opus 4" },
  { value: "openai/gpt-4o", label: "GPT-4o" },
  { value: "openai/o3", label: "O3" },
  { value: "google/gemini-2.5-pro", label: "Gemini 2.5 Pro" },
  { value: "openrouter/auto", label: "OpenRouter Auto" },
] as const;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function SandboxConfigEditor() {
  const { sandbox, pushConfig, error, clearError } = useSandboxStore();
  const { toast } = useToast();

  const [model, setModel] = useState("");
  const [autoCompaction, setAutoCompaction] = useState(true);
  const [guideEnabled, setGuideEnabled] = useState(true);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [saving, setSaving] = useState(false);

  // Hydrate form from sandbox config when available
  useEffect(() => {
    if (!sandbox?.config) return;
    const cfg = sandbox.config;
    if (cfg.model && typeof cfg.model === "string") setModel(cfg.model);
    if (typeof cfg.autoCompaction === "boolean") setAutoCompaction(cfg.autoCompaction);
    if (typeof cfg.guideEnabled === "boolean") setGuideEnabled(cfg.guideEnabled);
    if (typeof cfg.systemPrompt === "string") setSystemPrompt(cfg.systemPrompt);
  }, [sandbox?.config]);

  const isReady = sandbox?.state === "active";

  async function handleSave() {
    if (!sandbox?.id) return;
    setSaving(true);
    clearError();
    try {
      await pushConfig(sandbox.id, {
        model,
        autoCompaction,
        guideEnabled,
        systemPrompt: systemPrompt || undefined,
      });
      toast("Configuration saved", { variant: "success" });
    } catch {
      toast(error ?? "Failed to save configuration", { variant: "error" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card data-testid="sandbox-config-editor">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5 text-primary" />
          Sandbox Configuration
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {!isReady && (
          <div className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-950/40 px-4 py-3 text-sm text-amber-200">
            <AlertCircle className="h-4 w-4 shrink-0 text-amber-400" />
            Configuration can only be pushed when the sandbox is running.
          </div>
        )}

        <fieldset disabled={!isReady} className="space-y-4">
          {/* Model selection */}
          <Select
            label="Default Model"
            value={model}
            onChange={(e) => setModel(e.target.value)}
          >
            <option value="">Select model…</option>
            {MODEL_PRESETS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </Select>

          {/* Or custom model string */}
          <Input
            label="Custom Model (overrides preset)"
            placeholder="provider/model-name"
            value={model}
            onChange={(e) => setModel(e.target.value)}
          />

          {/* Toggles */}
          <div className="flex flex-col gap-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={autoCompaction}
                onChange={(e) => setAutoCompaction(e.target.checked)}
                className="h-4 w-4 rounded border-white/20 bg-surface text-primary focus:ring-primary/50"
                disabled={!isReady}
              />
              <div>
                <p className="text-sm font-medium text-foreground">
                  Auto-Compaction
                </p>
                <p className="text-xs text-muted-foreground">
                  Automatically manage context window when it fills up
                </p>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={guideEnabled}
                onChange={(e) => setGuideEnabled(e.target.checked)}
                className="h-4 w-4 rounded border-white/20 bg-surface text-primary focus:ring-primary/50"
                disabled={!isReady}
              />
              <div>
                <p className="text-sm font-medium text-foreground">
                  Guide Agent
                </p>
                <p className="text-xs text-muted-foreground">
                  Enable the meta-cognitive guide for context management
                </p>
              </div>
            </label>
          </div>

          {/* System prompt */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="system-prompt"
              className="text-sm font-medium text-foreground"
            >
              Custom System Prompt
            </label>
            <textarea
              id="system-prompt"
              rows={4}
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              placeholder="Optional system prompt to prepend to all sessions…"
              disabled={!isReady}
              className="w-full rounded-md border border-white/10 bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted/60 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-colors duration-150 resize-y"
            />
          </div>
        </fieldset>

        {/* Save button */}
        <Button
          onClick={handleSave}
          loading={saving}
          disabled={!isReady}
          size="md"
        >
          <Save className="h-4 w-4" />
          Save Configuration
        </Button>
      </CardContent>
    </Card>
  );
}
